import React from 'react';
import { CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import styles from './PlanCard.module.css';

const formatPrice = (price) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);

/**
 * Card de um plano: nome, para quem é, preço, botão e destaques curtos.
 *
 * @param {Object}   plan
 * @param {Object}   state       — getPlanCardState(plan, ...)
 * @param {boolean}  featured    — selo "Mais escolhido"
 * @param {Array}    highlights  — [{ text, third }]
 * @param {string}   intro       — "Tudo do Essencial, e mais:" (planos acima do primeiro)
 * @param {Function} onSelect
 * @param {Object}   discount    — { price, note } reconquista do teste vencido ou condição de parceiro (opcional)
 */
const PlanCard = ({ plan, state, featured, highlights, intro, onSelect, discount }) => {
  const { isCurrent, isProcessing, ctaText, ctaDisabled, ctaLocked, notes } = state;

  const cardClasses = [
    styles.card,
    isCurrent ? styles.cardCurrent : '',
    featured && !isCurrent ? styles.cardFeatured : '',
  ].filter(Boolean).join(' ');

  const buttonClasses = [
    styles.cta,
    plan.contact_sales ? styles.ctaContact : '',
    isCurrent ? styles.ctaCurrent : '',
    !isCurrent && !plan.contact_sales && !featured && !state.isUpgrade ? styles.ctaSecondary : '',
  ].filter(Boolean).join(' ');

  return (
    <article className={cardClasses}>
      {isCurrent && (
        <span className={`${styles.badge} ${styles.badgeCurrent}`}>
          <CheckIcon className={styles.badgeIcon} /> Seu plano
        </span>
      )}
      {featured && !isCurrent && (
        <span className={`${styles.badge} ${styles.badgeFeatured}`}>Mais escolhido</span>
      )}

      <h3 className={`${styles.name} ${plan.contact_sales ? styles.nameContact : ''}`}>{plan.name}</h3>
      {plan.description && <p className={styles.description}>{plan.description}</p>}

      <div className={styles.priceRow}>
        {plan.contact_sales ? (
          <span className={`${styles.price} ${styles.priceContact}`}>Sob consulta</span>
        ) : (
          <>
            {discount && <s className={styles.priceOriginal}>{formatPrice(plan.price)}</s>}
            <span className={styles.price}>{formatPrice(discount ? discount.price : plan.price)}</span>
            <span className={styles.period}>/mês</span>
          </>
        )}
      </div>
      {discount && !plan.contact_sales && (
        <p className={styles.discountNote}>{discount.note}</p>
      )}

      <button
        type="button"
        className={buttonClasses}
        onClick={() => onSelect(plan)}
        disabled={ctaDisabled}
      >
        {isProcessing ? (
          <>
            <CircularProgress size={14} sx={{ color: 'inherit', mr: 0.75 }} />
            Processando...
          </>
        ) : (
          <>
            {ctaLocked && <LockOutlinedIcon className={styles.ctaLock} />}
            {ctaText}
          </>
        )}
      </button>

      {notes.map((note) => (
        <p key={note} className={styles.note}>{note}</p>
      ))}

      {highlights.length > 0 && (
        <div className={styles.highlights}>
          {intro && <p className={styles.intro}>{intro}</p>}
          <ul className={styles.list}>
            {highlights.map((item) => (
              <li key={item.text} className={styles.item}>
                <CheckIcon className={styles.itemIcon} />
                <span>{item.text}{item.third ? '*' : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
};

export default PlanCard;
